import React, { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Search, Upload, ScanLine, Image as ImageIcon, ArrowRight, MapPin, Calendar, Users, Zap, Share2, BarChart3, Download, Camera, Sparkles, Mail, Phone } from "lucide-react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import api from "../lib/api";

const HERO_IMAGE = "https://images.unsplash.com/photo-1550344532-c8d65ad8c477?auto=format&fit=crop&w=1400&q=80";

export default function Landing() {
  const navigate = useNavigate();
  const location = useLocation();
  const [events, setEvents] = useState([]);
  const [bib, setBib] = useState("");
  const [selectedSlug, setSelectedSlug] = useState("");

  useEffect(() => {
    api.get("/public/events").then((res) => {
      setEvents(res.data);
      if (res.data.length > 0) setSelectedSlug(res.data[0].slug);
    }).catch(() => {});
  }, []);

  // Scroll to in-page section when the hash changes (e.g. clicking nav menu while on landing)
  useEffect(() => {
    if (!location.hash) {
      window.scrollTo({ top: 0, behavior: "instant" in window ? "instant" : "auto" });
      return;
    }
    const id = location.hash.slice(1);
    // Defer to next frame so target element is mounted
    const t = setTimeout(() => {
      const el = document.getElementById(id);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 50);
    return () => clearTimeout(t);
  }, [location.hash, events.length]);

  const activeEvents = useMemo(() => events.filter((e) => e.photo_count > 0).slice(0, 6), [events]);
  const allEvents = events;

  const handleHeroSearch = (e) => {
    e.preventDefault();
    if (!selectedSlug) return;
    const url = `/events/${selectedSlug}${bib ? `?bib=${encodeURIComponent(bib)}` : ""}`;
    navigate(url);
  };

  return (
    <div className="min-h-screen flex flex-col bg-white pb-16 md:pb-0">
      <Navbar />

      {/* HERO */}
      <section className="border-b border-[#E2E8F0]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-20 lg:py-24 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12 items-center">
          <div className="lg:col-span-7 rs-fade-in">
            <p className="kicker mb-4 md:mb-5" data-testid="hero-kicker">PAN-AFRICAN RACE PHOTO DISCOVERY</p>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold text-[#0F172A] leading-[1.05] tracking-tighter">
              Enter your bib.<br />
              <span className="text-[#5B4CFF]">Find every photo.</span>
            </h1>
            <p className="mt-5 md:mt-6 text-base md:text-lg text-[#475569] max-w-xl leading-relaxed">
              Type your race number, tap your event, get every captured moment instantly. No account, no wait.
            </p>

            {/* Bib-first search */}
            <form
              onSubmit={handleHeroSearch}
              className="mt-7 md:mt-8"
              data-testid="hero-search-form"
            >
              <label className="block text-[10px] font-mono tracking-widest text-[#5B4CFF] mb-2">
                YOUR RACE NUMBER
              </label>
              <div className="flex flex-col sm:flex-row gap-3">
                <input
                  type="text"
                  inputMode="numeric"
                  autoFocus
                  placeholder="0000"
                  value={bib}
                  onChange={(e) => setBib(e.target.value)}
                  className="flex-1 min-w-0 bg-white border-2 border-[#0F172A] focus:border-[#5B4CFF] px-5 py-4 md:py-5 text-4xl sm:text-5xl md:text-6xl font-mono font-extrabold text-[#0F172A] tracking-tight focus:outline-none"
                  data-testid="hero-bib-input"
                  aria-label="Race number"
                />
                <button
                  type="submit"
                  disabled={!selectedSlug || !bib.trim()}
                  className="bg-[#5B4CFF] hover:bg-[#4a3dde] disabled:opacity-40 disabled:cursor-not-allowed text-white font-semibold px-6 py-4 inline-flex items-center justify-center gap-2 transition-colors min-h-[56px] sm:min-h-0 sm:w-56"
                  data-testid="hero-search-btn"
                >
                  <Search size={18} /> Find my photos
                </button>
              </div>

              {/* Live events chip strip */}
              <div className="mt-6" data-testid="live-events-strip">
                <div className="flex items-center gap-2 mb-3">
                  <span className="w-1.5 h-1.5 bg-[#10B981] rounded-full animate-pulse" />
                  <p className="text-[10px] font-mono tracking-widest text-[#475569]">
                    LIVE EVENTS · SELECT YOURS
                  </p>
                </div>
                {allEvents.length === 0 ? (
                  <p className="text-sm text-[#475569] italic" data-testid="hero-no-events">
                    No events published yet. Check back soon.
                  </p>
                ) : (
                  <div className="flex flex-wrap gap-2">
                    {allEvents.map((ev) => {
                      const active = selectedSlug === ev.slug;
                      return (
                        <button
                          key={ev.id}
                          type="button"
                          onClick={() => setSelectedSlug(ev.slug)}
                          className={`inline-flex items-center gap-2 px-3 py-2 text-sm border transition-colors ${
                            active
                              ? "bg-[#0F172A] text-white border-[#0F172A]"
                              : "bg-white text-[#0F172A] border-[#E2E8F0] hover:border-[#5B4CFF]"
                          }`}
                          data-testid={`live-event-${ev.slug}`}
                        >
                          <span className="font-semibold">{ev.name}</span>
                          <span className={`text-[10px] font-mono ${active ? "text-white/70" : "text-[#475569]"}`}>
                            · {ev.city}
                          </span>
                          {ev.photo_count > 0 && (
                            <span className={`text-[10px] font-mono ml-1 ${active ? "bg-white/20 text-white" : "bg-[#F8FAFC] text-[#5B4CFF]"} px-1.5 py-0.5`}>
                              {ev.photo_count}
                            </span>
                          )}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            </form>

            <div className="mt-6 flex flex-wrap items-center gap-x-6 gap-y-2 text-xs sm:text-sm text-[#475569]">
              <span className="inline-flex items-center gap-2"><Zap size={14} className="text-[#5B4CFF]" /> Instant results</span>
              <span className="inline-flex items-center gap-2"><Download size={14} className="text-[#5B4CFF]" /> Free downloads</span>
              <span className="inline-flex items-center gap-2"><Share2 size={14} className="text-[#5B4CFF]" /> Easy sharing</span>
            </div>

            <div className="mt-8 md:mt-10 flex flex-wrap gap-3 md:gap-4">
              <a
                href="#active-events"
                className="inline-flex items-center gap-2 text-sm font-semibold text-[#0F172A] border border-[#E2E8F0] hover:border-[#5B4CFF] px-5 py-3 transition-colors"
                data-testid="hero-browse-events-btn"
              >
                Browse all events <ArrowRight size={14} />
              </a>
              <Link
                to="/login"
                className="inline-flex items-center gap-2 text-sm font-semibold text-[#5B4CFF] hover:underline px-1 py-3"
                data-testid="hero-organize-btn"
              >
                Organize an Event <ArrowRight size={14} />
              </Link>
            </div>
          </div>

          <div className="lg:col-span-5 rs-fade-in order-first lg:order-last">
            <div className="relative border border-[#E2E8F0] overflow-hidden aspect-[4/5] max-w-md mx-auto lg:max-w-none">
              <img
                src={HERO_IMAGE}
                alt="Runner crossing finish line"
                className="w-full h-full object-cover"
                data-testid="hero-image"
              />
              <div className="absolute left-3 top-3 sm:left-4 sm:top-4 bg-white border border-[#E2E8F0] px-3 py-2">
                <p className="text-[10px] font-mono tracking-widest text-[#475569]">LIVE EVENT</p>
                <p className="text-sm font-semibold text-[#0F172A]">
                  {activeEvents[0]?.name || "Gaborone Marathon 2027"}
                </p>
              </div>
              <div className="absolute right-3 bottom-3 sm:right-4 sm:bottom-4 bg-[#5B4CFF] text-white px-4 py-3">
                <p className="text-[10px] font-mono tracking-widest opacity-80">BIB #</p>
                <p className="text-xl font-extrabold font-mono">4271</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ACTIVE EVENTS — direct quick search per card */}
      <section id="active-events" className="border-b border-[#E2E8F0] bg-[#F8FAFC]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-20">
          <div className="flex items-end justify-between flex-wrap gap-4 mb-8 md:mb-10">
            <div>
              <p className="kicker mb-2 inline-flex items-center gap-2">
                <Sparkles size={12} /> ACTIVE EVENTS
              </p>
              <h2 className="font-display text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold tracking-tighter text-[#0F172A]">
                Search photos by race
              </h2>
            </div>
            <p className="text-sm text-[#475569] max-w-md leading-relaxed">
              Pick your race, type your bib, and we&apos;ll surface every photo of you.
            </p>
          </div>

          {events.length === 0 ? (
            <div className="border border-dashed border-[#E2E8F0] p-10 md:p-14 text-center bg-white" data-testid="active-empty">
              <Camera size={28} className="mx-auto text-[#5B4CFF] mb-4" />
              <p className="font-display text-lg md:text-xl text-[#0F172A] mb-2">No published events yet</p>
              <p className="text-sm text-[#475569]">Check back soon — new races publish weekly.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6" data-testid="active-events-grid">
              {events.map((ev) => (
                <ActiveEventCard key={ev.id} ev={ev} />
              ))}
            </div>
          )}
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section id="how-it-works" className="border-b border-[#E2E8F0]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-20 lg:py-24">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-10 items-end mb-10 md:mb-12">
            <div className="lg:col-span-7">
              <p className="kicker mb-2 md:mb-3">HOW IT WORKS</p>
              <h2 className="font-display text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold tracking-tighter text-[#0F172A]">
                Four steps from finish line<br className="hidden sm:block" /> to download folder.
              </h2>
            </div>
            <p className="lg:col-span-5 text-sm md:text-base text-[#475569] leading-relaxed">
              Photographers upload photos. Our OCR engine reads every visible bib number. Runners search and download. No login, no friction.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 border border-[#E2E8F0]">
            {[
              { n: "01", label: "Select Event", desc: "Pick from races published on RaceSnap.", icon: MapPin },
              { n: "02", label: "Enter Race Number", desc: "Type your bib digits and hit search.", icon: ScanLine },
              { n: "03", label: "View Your Photos", desc: "We surface every photo of you instantly.", icon: ImageIcon },
              { n: "04", label: "Download & Share", desc: "Save originals or share to socials.", icon: Download },
            ].map((step, idx) => {
              const Icon = step.icon;
              return (
                <div
                  key={step.n}
                  className={`p-6 md:p-8 bg-white ${idx > 0 ? "sm:border-l lg:border-l border-[#E2E8F0]" : ""} ${idx >= 2 ? "sm:border-t lg:border-t-0" : ""} ${idx === 1 || idx === 3 ? "sm:border-l" : ""}`}
                  data-testid={`step-card-${idx}`}
                >
                  <p className="font-mono text-xs tracking-widest text-[#5B4CFF]">{step.n}</p>
                  <div className="mt-5 md:mt-6 w-10 h-10 bg-[#5B4CFF] text-white flex items-center justify-center">
                    <Icon size={20} />
                  </div>
                  <h3 className="mt-4 md:mt-5 font-display text-lg md:text-xl font-semibold text-[#0F172A]">{step.label}</h3>
                  <p className="mt-2 text-sm text-[#475569] leading-relaxed">{step.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* BENEFITS */}
      <section id="benefits" className="border-b border-[#E2E8F0] bg-[#F8FAFC]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-20 lg:py-24 grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-8">
          <div className="bg-white border border-[#E2E8F0] p-6 md:p-10">
            <p className="kicker mb-3 md:mb-4">FOR RUNNERS</p>
            <h3 className="font-display text-xl sm:text-2xl md:text-3xl font-bold text-[#0F172A]">Your race memories, in one tap.</h3>
            <ul className="mt-6 md:mt-8 space-y-3 md:space-y-4">
              {["Instant photo discovery", "Fast, full-resolution downloads", "Mobile-friendly experience", "Easy social sharing"].map((b) => (
                <li key={b} className="flex items-start gap-3 text-[#0F172A]">
                  <span className="mt-1.5 w-1.5 h-1.5 bg-[#5B4CFF] shrink-0" />
                  <span className="text-sm leading-relaxed">{b}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="bg-white border border-[#E2E8F0] p-6 md:p-10">
            <p className="kicker mb-3 md:mb-4">FOR ORGANIZERS</p>
            <h3 className="font-display text-xl sm:text-2xl md:text-3xl font-bold text-[#0F172A]">Centralized photo logistics, zero IT cost.</h3>
            <ul className="mt-6 md:mt-8 space-y-3 md:space-y-4">
              {[
                { icon: Users, t: "Better participant experience" },
                { icon: ImageIcon, t: "Centralized photo management" },
                { icon: BarChart3, t: "Increased engagement & analytics" },
                { icon: Upload, t: "Sponsor visibility on every photo" },
              ].map(({ icon: Icon, t }) => (
                <li key={t} className="flex items-center gap-3 text-[#0F172A]">
                  <Icon size={16} className="text-[#5B4CFF]" />
                  <span className="text-sm">{t}</span>
                </li>
              ))}
            </ul>
            <Link
              to="/login"
              className="mt-6 md:mt-8 inline-flex items-center gap-2 text-sm font-semibold text-[#0F172A] border border-[#0F172A] hover:bg-[#0F172A] hover:text-white px-5 py-3 transition-colors"
              data-testid="benefits-organize-btn"
            >
              Organize an event <ArrowRight size={14} />
            </Link>
          </div>
        </div>
      </section>

      {/* CONTACT */}
      <section id="contact-section" className="border-b border-[#E2E8F0]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-20 lg:py-24 grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12">
          <div className="lg:col-span-5">
            <p className="kicker mb-2 md:mb-3">CONTACT</p>
            <h2 className="font-display text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold tracking-tighter text-[#0F172A]">
              Run an event or have a question?
            </h2>
            <p className="mt-4 md:mt-6 text-sm md:text-base text-[#475569] leading-relaxed max-w-md">
              Reach out to set up a new race, request press access, or partner with the platform. We typically reply within one business day.
            </p>
          </div>

          <div className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-4">
            <a
              href="mailto:events@racesnap.co.bw"
              className="group bg-white border border-[#E2E8F0] hover:border-[#5B4CFF] p-6 md:p-8 transition-colors flex flex-col gap-4"
              data-testid="contact-email-card"
            >
              <div className="w-10 h-10 bg-[#5B4CFF] text-white flex items-center justify-center">
                <Mail size={20} />
              </div>
              <div>
                <p className="kicker mb-1">EMAIL</p>
                <p className="font-display text-base md:text-lg font-semibold text-[#0F172A] group-hover:text-[#5B4CFF] transition-colors break-all">
                  events@racesnap.co.bw
                </p>
                <p className="mt-2 text-xs text-[#475569]">For event setup, partnerships and support.</p>
              </div>
            </a>

            <a
              href="tel:+26771835283"
              className="group bg-white border border-[#E2E8F0] hover:border-[#5B4CFF] p-6 md:p-8 transition-colors flex flex-col gap-4"
              data-testid="contact-phone-card"
            >
              <div className="w-10 h-10 bg-[#5B4CFF] text-white flex items-center justify-center">
                <Phone size={20} />
              </div>
              <div>
                <p className="kicker mb-1">MOBILE</p>
                <p className="font-display text-base md:text-lg font-semibold text-[#0F172A] group-hover:text-[#5B4CFF] transition-colors font-mono">
                  +267 7183 5283
                </p>
                <p className="mt-2 text-xs text-[#475569]">Voice & WhatsApp · Mon – Sat, 09:00 – 18:00 CAT.</p>
              </div>
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}

function ActiveEventCard({ ev }) {
  const [bib, setBib] = useState("");
  const navigate = useNavigate();

  const submit = (e) => {
    e.preventDefault();
    const url = `/events/${ev.slug}${bib ? `?bib=${encodeURIComponent(bib)}` : ""}`;
    navigate(url);
  };

  return (
    <div className="group bg-white border border-[#E2E8F0] hover:border-[#5B4CFF] transition-colors flex flex-col" data-testid={`event-card-${ev.slug}`}>
      <Link to={`/events/${ev.slug}`} className="block">
        <div className="aspect-[16/10] bg-[#F8FAFC] border-b border-[#E2E8F0] overflow-hidden">
          {ev.banner_url ? (
            <img src={ev.banner_url} alt={ev.name} className="w-full h-full object-cover group-hover:scale-[1.02] transition-transform duration-500" />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-[#5B4CFF]">
              <ImageIcon size={28} />
            </div>
          )}
        </div>
      </Link>
      <div className="p-5 md:p-6 flex flex-col gap-4 flex-1">
        <div>
          <p className="text-[11px] font-mono tracking-widest text-[#5B4CFF] mb-1.5">
            {ev.city?.toUpperCase()} · {ev.country?.toUpperCase()}
          </p>
          <Link to={`/events/${ev.slug}`} className="font-display text-lg md:text-xl font-semibold text-[#0F172A] group-hover:text-[#5B4CFF] transition-colors block leading-tight">
            {ev.name}
          </Link>
          <div className="mt-3 flex items-center gap-4 text-xs text-[#475569]">
            <span className="inline-flex items-center gap-1.5"><Calendar size={12} />{ev.event_date}</span>
            <span className="inline-flex items-center gap-1.5"><ImageIcon size={12} />{ev.photo_count} photos</span>
          </div>
        </div>

        <form onSubmit={submit} className="mt-auto flex gap-2" data-testid={`card-search-${ev.slug}`}>
          <input
            type="text"
            inputMode="numeric"
            placeholder="Bib #"
            value={bib}
            onChange={(e) => setBib(e.target.value)}
            className="flex-1 bg-white border border-[#E2E8F0] px-3 py-2.5 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-[#5B4CFF]"
            data-testid={`card-bib-${ev.slug}`}
          />
          <button
            type="submit"
            className="bg-[#5B4CFF] hover:bg-[#4a3dde] text-white font-semibold px-4 py-2.5 text-sm inline-flex items-center gap-1.5 transition-colors"
            data-testid={`card-go-${ev.slug}`}
          >
            <Search size={14} /> Find
          </button>
        </form>
      </div>
    </div>
  );
}
